"use client";

import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Button } from "../../ui/button";
import { useArchivalMode } from "@/contexts/ArchivalModeContext";
import { getFY_YEAR } from "@/utils/sharepoint-env";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";
import { Label } from "../../ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../ui/select";
import { Input } from "../../ui/input";
import {
  AlertCircle,
  ArrowLeft,
  CheckCircle,
  ChevronDown,
  Loader2,
  MessageSquare,
  Play,
  Save,
  Search,
  Upload,
  Pencil,
} from "lucide-react";
import { ReviewComment } from "../../shared/ReviewComment";
import { DocumentManager } from "../../planning/DocumentManager";
import { getCompanyName } from "@/lib/textron-data-processor";
import {
  getProcedureFileUsageForWorkspace,
  updateProcedureFileUsageForWorkspace,
  type FileUsageEntry,
} from "@/utils/fileUsageReference";
import {
  computeRouLeaseDepositColumns,
  parseRepDateDdMmYyyy,
} from "@/utils/rouDisclosureLeaseDepositCompute";

const FILE_SECTION = "Execution_ROU_File";
const CONFIG_FILE = "Execution_ROU_DISCLOSURE_Config.json";
const WORKSPACE_NAME = "Right-of-Use Assets";
const PROCEDURE_NAME = "ROU Disclosure";
const UNMAP_VALUE = "__unmap__";
const NO_FILE_VALUE = "__no_file__";
const USEFUL_LIFE_UNIT_NONE = "__useful_life_none__";
const DISCLOSURE_OUTPUT_FILE = "Execution_ROU_Disclosure.json";

const DISCLOSURE_ROW_DEFS: Array<{
  id: string;
  label: string;
  section?: boolean;
  derived?: boolean;
}> = [
  { id: "_sec_gb", label: "Gross Block", section: true },
  { id: "gb_opening", label: "Opening Balance" },
  { id: "gb_additions", label: "Additions" },
  {
    id: "gb_transfers",
    label:
      "ROU adjustment — lease modification / reassessment (Ind AS journals, current FY, signed)",
  },
  { id: "gb_closing", label: "Closing Balance", derived: true },
  { id: "_sec_ad", label: "Accumulated Depreciation", section: true },
  { id: "ad_opening", label: "Opening Balance" },
  { id: "ad_depreciation", label: "Depreciation for the year" },
  { id: "ad_eliminations", label: "Eliminations/ Reclassification" },
  { id: "ad_closing", label: "Closing Balance", derived: true },
  { id: "net_block", label: "Net block", derived: true },
];

const DATA_ROW_IDS = [
  "gb_opening",
  "gb_additions",
  "gb_transfers",
  "ad_opening",
  "ad_depreciation",
  "ad_eliminations",
] as const;

const SPECIAL_COLS = ["leases", "deposits"] as const;

interface ExcelColumn {
  name: string;
  value: string;
}

interface RightOfUseDisclosureProcedureProps {
  setActiveSection?: (section: string) => void;
}

const REGISTER_FIELDS: Array<{ key: string; label: string }> = [
  { key: "asset_code", label: "Asset Code" },
  { key: "asset_category_fs", label: "Asset Category (as per FS note)" },
  { key: "asset_description", label: "Asset Description" },
  { key: "useful_life", label: "Useful Life" },
  { key: "original_cost", label: "Original Cost" },
  { key: "accumulated_depreciation", label: "Accumulated Depreciation" },
  { key: "net_book_value", label: "Net book value" },
  { key: "capitalization_date", label: "Capitalization Date" },
  { key: "scrap_value", label: "Scrap Value" },
  { key: "additions", label: "Additions" },
  { key: "deletions_gross_block", label: "Deletions (gross block)" },
];

const ADDITIONS_FIELDS: Array<{ key: string; label: string }> = [
  { key: "asset_code", label: "Asset Code" },
  { key: "asset_category_fs", label: "Asset Category (as per FS note)" },
  { key: "asset_description", label: "Asset Description" },
  { key: "original_cost", label: "Original Cost" },
  { key: "capitalization_date", label: "Capitalization Date" },
];

const DELETIONS_FIELDS: Array<{ key: string; label: string }> = [
  { key: "asset_code", label: "Asset Code" },
  {
    key: "asset_category_fs",
    label: "Asset Category (as per FS note) (for useful life testing)",
  },
  { key: "asset_description", label: "Asset Description" },
  { key: "original_cost", label: "Original Cost" },
  { key: "accumulated_depreciation", label: "Accumulated Depreciation" },
  { key: "net_book_value", label: "Net book value" },
  { key: "capitalization_date", label: "Capitalization Date" },
  { key: "scrap_value", label: "Scrap Value" },
  { key: "selling_date", label: "Selling Date" },
];

type PanelKey = "register" | "additions" | "deletions";

function getSectionQuestionId(sectionName: string): string {
  const normalized = sectionName
    .replace(/[^a-zA-Z0-9\s]/g, "")
    .replace(/\s+/g, "_")
    .toUpperCase();
  return `ROU_DISCLOSURE_${normalized}`;
}

function formatIntComma(n: number): string {
  return new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(
    Number.isFinite(n) ? Math.round(n) : 0,
  );
}

function applyDerivedRows(
  m: Record<string, Record<string, number>>,
  categoryCols: string[],
): void {
  const cols = [...categoryCols, ...SPECIAL_COLS];
  for (const c of cols) {
    const gbO = m.gb_opening?.[c] ?? 0;
    const gbA = m.gb_additions?.[c] ?? 0;
    const gbT = m.gb_transfers?.[c] ?? 0;
    if (!m.gb_closing) m.gb_closing = {};
    m.gb_closing[c] = c === "leases" ? gbO + gbA + gbT : gbO + gbA - gbT;

    const adO = m.ad_opening?.[c] ?? 0;
    const adD = m.ad_depreciation?.[c] ?? 0;
    const adE = m.ad_eliminations?.[c] ?? 0;
    if (!m.ad_closing) m.ad_closing = {};
    m.ad_closing[c] = adO + adD + adE;

    if (!m.net_block) m.net_block = {};
    m.net_block[c] = (m.gb_closing[c] ?? 0) - (m.ad_closing[c] ?? 0);
  }
}

function ensureMatrixShape(
  m: Record<string, Record<string, number>>,
  categoryCols: string[],
): Record<string, Record<string, number>> {
  const cols = [...categoryCols, ...SPECIAL_COLS];
  const allRows = [...DATA_ROW_IDS, "gb_closing", "ad_closing", "net_block"];
  const next = { ...m };
  for (const r of allRows) {
    next[r] = { ...(next[r] || {}) };
    for (const c of cols) {
      const v = next[r][c];
      if (v === undefined || Number.isNaN(Number(v))) next[r][c] = 0;
    }
  }
  return next;
}

function inferDisclosureCategoriesFromMatrix(
  matrix: Record<string, Record<string, number>>,
): string[] {
  const go = matrix.gb_opening;
  if (!go || typeof go !== "object") return [];
  return Object.keys(go)
    .filter((k) => k !== "leases" && k !== "deposits")
    .sort();
}

/** Coerce JSON cell values to rounded numbers for table state. */
function sanitizeCellMatrix(raw: unknown): Record<string, Record<string, number>> {
  const out: Record<string, Record<string, number>> = {};
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return out;
  for (const [rowKey, colMap] of Object.entries(raw as Record<string, unknown>)) {
    if (!colMap || typeof colMap !== "object" || Array.isArray(colMap)) continue;
    out[rowKey] = {};
    for (const [colKey, val] of Object.entries(colMap as Record<string, unknown>)) {
      const n = typeof val === "number" && Number.isFinite(val) ? val : Number(val);
      out[rowKey][colKey] = Number.isFinite(n) ? Math.round(n) : 0;
    }
  }
  return out;
}

function hasUsableDisclosureTableFromConfig(dt: {
  categories?: unknown;
  cellMatrix?: unknown;
} | null | undefined): boolean {
  if (!dt || typeof dt !== "object") return false;
  const cm = dt.cellMatrix;
  if (!cm || typeof cm !== "object" || Array.isArray(cm)) return false;
  if (Object.keys(cm as object).length === 0) return false;
  const cats = dt.categories;
  if (Array.isArray(cats) && cats.some((c) => String(c ?? "").trim().length > 0)) return true;
  return inferDisclosureCategoriesFromMatrix(sanitizeCellMatrix(cm)).length > 0;
}

function pickDisclosureEntry(list: unknown[]): Record<string, unknown> | null {
  if (!Array.isArray(list) || list.length === 0) return null;
  let best: Record<string, unknown> | null = null;
  let bestT = -Infinity;
  for (const e of list) {
    if (!e || typeof e !== "object") continue;
    const rd = String((e as Record<string, unknown>).reporting_date ?? "");
    const t = parseRepDateDdMmYyyy(rd)?.getTime() ?? -Infinity;
    if (t >= bestT) {
      bestT = t;
      best = e as Record<string, unknown>;
    }
  }
  return best;
}

/** Normalize API payload: array of periods, or a single period object. */
function disclosureJsonToList(raw: unknown): unknown[] {
  if (Array.isArray(raw)) return raw;
  if (raw && typeof raw === "object" && !Array.isArray(raw)) {
    const o = raw as Record<string, unknown>;
    if ("data" in o && typeof o.data === "object") return [raw];
  }
  return [];
}

/**
 * Build table state from Execution_ROU_Disclosure.json (latest reporting_date).
 * Category columns come from JSON; leases/deposits rows can be kept from saved config when JSON omits them.
 */
function buildDisclosureTableStateFromExecutionJson(
  raw: unknown,
  savedCellMatrix: Record<string, Record<string, number>> | undefined,
): {
  categories: string[];
  cellMatrix: Record<string, Record<string, number>>;
  reportingDate: string | null;
} | null {
  const list = disclosureJsonToList(raw);
  const entry = pickDisclosureEntry(list);
  const data = entry?.data;
  if (!data || typeof data !== "object" || Array.isArray(data)) return null;
  const dataObj = data as Record<string, Record<string, unknown>>;
  const cats = Object.keys(dataObj).filter((k) => {
    const v = dataObj[k];
    return v != null && typeof v === "object" && !Array.isArray(v);
  });
  if (cats.length === 0) return null;
  cats.sort();

  const base = ensureMatrixShape(savedCellMatrix || {}, cats);
  for (const cat of cats) {
    const block = dataObj[cat] as Record<string, Record<string, unknown>> | undefined;
    const gb = (block?.gross_block || {}) as Record<string, number>;
    const ad = (block?.accumulated_depreciation || {}) as Record<string, number>;
    base.gb_opening[cat] = Math.round(Number(gb.opening_balance) || 0);
    base.gb_additions[cat] = Math.round(Number(gb.additions) || 0);
    base.gb_transfers[cat] = Math.round(Number(gb.transfers_disposals) || 0);
    base.ad_opening[cat] = Math.round(Number(ad.opening_balance) || 0);
    base.ad_depreciation[cat] = Math.round(Number(ad.depreciation_for_year) || 0);
    base.ad_eliminations[cat] = Math.round(Number(ad.eliminations_reclassification) || 0);
  }

  for (const r of DATA_ROW_IDS) {
    for (const sp of SPECIAL_COLS) {
      const v = savedCellMatrix?.[r]?.[sp];
      if (v !== undefined && Number.isFinite(Number(v))) {
        base[r][sp] = Math.round(Number(v));
      }
    }
  }

  applyDerivedRows(base, cats);
  return {
    categories: cats,
    cellMatrix: base,
    reportingDate: String(entry?.reporting_date ?? "") || null,
  };
}

function buildScriptColumnMap(
  mappings: Record<string, string>,
  keys: string[],
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const k of keys) {
    const v = mappings[k];
    out[k] = v && String(v).trim() ? String(v).trim() : "";
  }
  return out;
}

/** One asset column or leases/deposits — matches Execution_ROU_Disclosure.json shape */
function disclosureColumnBlock(
  m: Record<string, Record<string, number>>,
  col: string,
): {
  gross_block: {
    opening_balance: number;
    additions: number;
    transfers_disposals: number;
  };
  accumulated_depreciation: {
    opening_balance: number;
    depreciation_for_year: number;
    eliminations_reclassification: number;
  };
} {
  return {
    gross_block: {
      opening_balance: Math.round(m.gb_opening?.[col] ?? 0),
      additions: Math.round(m.gb_additions?.[col] ?? 0),
      transfers_disposals: Math.round(m.gb_transfers?.[col] ?? 0),
    },
    accumulated_depreciation: {
      opening_balance: Math.round(m.ad_opening?.[col] ?? 0),
      depreciation_for_year: Math.round(m.ad_depreciation?.[col] ?? 0),
      eliminations_reclassification: Math.round(m.ad_eliminations?.[col] ?? 0),
    },
  };
}

function buildExecutionRouDisclosureEntry(
  reportingDate: string,
  m: Record<string, Record<string, number>>,
  categories: string[],
): Record<string, unknown> {
  const data: Record<string, unknown> = {};
  for (const cat of categories) {
    data[cat] = disclosureColumnBlock(m, cat);
  }
  return {
    reporting_date: reportingDate,
    data,
    leases: disclosureColumnBlock(m, "leases"),
    deposits: disclosureColumnBlock(m, "deposits"),
  };
}

/** Valid disclosure periods only (skip empty `{}` from missing file). */
function normalizeDisclosureList(existing: unknown): Record<string, unknown>[] {
  if (existing == null) return [];
  if (Array.isArray(existing)) {
    return existing.filter(
      (x): x is Record<string, unknown> =>
        x != null &&
        typeof x === "object" &&
        !Array.isArray(x) &&
        String((x as Record<string, unknown>).reporting_date ?? "").trim().length > 0,
    ) as Record<string, unknown>[];
  }
  if (
    typeof existing === "object" &&
    !Array.isArray(existing) &&
    String((existing as Record<string, unknown>).reporting_date ?? "").trim().length > 0
  ) {
    return [existing as Record<string, unknown>];
  }
  return [];
}

/** Same reporting_date (trimmed) → drop all matching periods, then append newEntry; different date → append. */
function mergeDisclosureJsonList(
  existing: unknown,
  newEntry: Record<string, unknown>,
): unknown[] {
  const rd = String(newEntry.reporting_date ?? "").trim();
  const list = normalizeDisclosureList(existing);
  const kept = list.filter((item) => String(item.reporting_date ?? "").trim() !== rd);
  kept.push(newEntry);
  return kept;
}

export default function RightOfUseDisclosureProcedure({
  setActiveSection,
}: RightOfUseDisclosureProcedureProps) {
  const { isArchivalMode } = useArchivalMode();

  const [fileSearchStatus, setFileSearchStatus] = useState<
    "idle" | "searching" | "found" | "not_found"
  >("idle");
  const [foundFiles, setFoundFiles] = useState<string[]>([]);
  const [referenceValue, setReferenceValue] = useState("");
  const [uploadStatus, setUploadStatus] = useState<
    "idle" | "uploading" | "completed" | "error"
  >("idle");

  const [selectedFiles, setSelectedFiles] = useState<Record<PanelKey, string>>({
    register: "",
    additions: "",
    deletions: "",
  });
  const [excelColumns, setExcelColumns] = useState<Record<PanelKey, ExcelColumn[]>>({
    register: [],
    additions: [],
    deletions: [],
  });
  const [loadingCols, setLoadingCols] = useState<Record<PanelKey, boolean>>({
    register: false,
    additions: false,
    deletions: false,
  });
  const [columnMappings, setColumnMappings] = useState<
    Record<PanelKey, Record<string, string>>
  >({
    register: {},
    additions: {},
    deletions: {},
  });
  const [usefulLifeUnit, setUsefulLifeUnit] = useState<"" | "months" | "years">("");

  const [saveStatus, setSaveStatus] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const [saveError, setSaveError] = useState<string | null>(null);
  const [processingStatus, setProcessingStatus] = useState<
    "idle" | "running" | "completed" | "error"
  >("idle");

  const [reviewCommentQuestionId, setReviewCommentQuestionId] = useState<string | null>(null);
  const [fileUsageReference, setFileUsageReference] = useState<FileUsageEntry[]>([]);
  const [companyName, setCompanyName] = useState("");
  const [inferenceQuestion, setInferenceQuestion] = useState<{
    textResponse: any;
    documents: any[];
  }>({ textResponse: null, documents: [] });
  const [inferenceSaveStatus, setInferenceSaveStatus] = useState<
    "idle" | "saving" | "saved" | "error"
  >("idle");
  const [inferenceSaveError, setInferenceSaveError] = useState<string | null>(null);

  const [disclosureCategories, setDisclosureCategories] = useState<string[]>([]);
  const [cellMatrix, setCellMatrix] = useState<Record<string, Record<string, number>>>({});
  const [disclosureRepDate, setDisclosureRepDate] = useState<string | null>(null);
  const [disclosureEditMode, setDisclosureEditMode] = useState(false);
  const [registerRunStatus, setRegisterRunStatus] = useState<"idle" | "running" | "ok" | "err">(
    "idle",
  );
  const [registerRunError, setRegisterRunError] = useState<string | null>(null);
  const [leaseRunStatus, setLeaseRunStatus] = useState<"idle" | "running" | "ok" | "err">("idle");
  const [leaseRunError, setLeaseRunError] = useState<string | null>(null);
  const [rouFilesSectionOpen, setRouFilesSectionOpen] = useState(false);

  const searchForFiles = useCallback(async () => {
    setFileSearchStatus("searching");
    try {
      if (window.sharePointAPI?.loadComcFiles) {
        const result = await window.sharePointAPI.loadComcFiles(FILE_SECTION);
        if (result.success && result.data?.files) {
          const files = result.data.files
            .map((f: any) => f.name)
            .filter((name: string) => name && name.trim().length > 0);
          if (files.length > 0) {
            setFoundFiles(files);
            setFileSearchStatus("found");
          } else {
            setFileSearchStatus("not_found");
          }
        } else {
          setFileSearchStatus("not_found");
        }
      } else {
        setFileSearchStatus("not_found");
      }
    } catch {
      setFileSearchStatus("not_found");
    }
  }, []);

  const loadFileUsageReference = useCallback(async () => {
    try {
      const usage = await getProcedureFileUsageForWorkspace(WORKSPACE_NAME, PROCEDURE_NAME);
      setFileUsageReference(usage);
    } catch (e) {
      console.error("Error loading file usage reference:", e);
    }
  }, []);

  const loadSavedConfig = useCallback(async () => {
    try {
      const api = (window as any).sharePointAPI;
      if (!api?.downloadClientJson) return;

      type SavedDisclosure = {
        categories?: string[];
        cellMatrix?: Record<string, Record<string, number>>;
        reportingDate?: string | null;
      };
      let savedDisclosure: SavedDisclosure | null = null;

      const result = await api.downloadClientJson(CONFIG_FILE);
      if (result?.success && result.data && typeof result.data === "object") {
        const d = result.data as any;
        if (d.register) {
          setSelectedFiles((prev) => ({
            ...prev,
            register: d.register.file || "",
          }));
          setColumnMappings((prev) => ({
            ...prev,
            register: d.register.mappings || {},
          }));
          if (d.register.usefulLifeUnit === "months" || d.register.usefulLifeUnit === "years") {
            setUsefulLifeUnit(d.register.usefulLifeUnit);
          } else {
            setUsefulLifeUnit("");
          }
        }
        if (d.additions) {
          setSelectedFiles((prev) => ({
            ...prev,
            additions: d.additions.file || "",
          }));
          setColumnMappings((prev) => ({
            ...prev,
            additions: d.additions.mappings || {},
          }));
        }
        if (d.deletions) {
          setSelectedFiles((prev) => ({
            ...prev,
            deletions: d.deletions.file || "",
          }));
          setColumnMappings((prev) => ({
            ...prev,
            deletions: d.deletions.mappings || {},
          }));
        }
        if (d.disclosureTable && typeof d.disclosureTable === "object") {
          savedDisclosure = d.disclosureTable as SavedDisclosure;
        }
      }

      const discRes = await api.downloadClientJson(DISCLOSURE_OUTPUT_FILE);

      if (hasUsableDisclosureTableFromConfig(savedDisclosure)) {
        const dt = savedDisclosure!;
        const sanitized = sanitizeCellMatrix(dt.cellMatrix);
        const cats =
          Array.isArray(dt.categories) && dt.categories.some((c) => String(c ?? "").trim())
            ? dt.categories.filter((c) => String(c ?? "").trim())
            : inferDisclosureCategoriesFromMatrix(sanitized);
        setDisclosureCategories(cats);
        setCellMatrix(ensureMatrixShape(sanitized, cats));
        setDisclosureRepDate(
          dt.reportingDate !== undefined && dt.reportingDate !== null
            ? String(dt.reportingDate)
            : null,
        );
      } else {
        const fromExecutionJson =
          discRes?.success && discRes.data !== undefined && discRes.data !== null
            ? buildDisclosureTableStateFromExecutionJson(
                discRes.data,
                savedDisclosure?.cellMatrix,
              )
            : null;

        if (fromExecutionJson) {
          setDisclosureCategories(fromExecutionJson.categories);
          setCellMatrix(fromExecutionJson.cellMatrix);
          setDisclosureRepDate(fromExecutionJson.reportingDate);
        } else if (savedDisclosure) {
          const dt = savedDisclosure;
          const sanitized = dt.cellMatrix ? sanitizeCellMatrix(dt.cellMatrix) : {};
          const cats =
            Array.isArray(dt.categories) && dt.categories.length > 0
              ? dt.categories
              : inferDisclosureCategoriesFromMatrix(sanitized);
          if (Object.keys(sanitized).length > 0 || cats.length > 0) {
            setDisclosureCategories(cats);
            setCellMatrix(ensureMatrixShape(sanitized, cats));
          }
          if (dt.reportingDate !== undefined) setDisclosureRepDate(dt.reportingDate);
        }
      }
    } catch {
      /* optional file */
    }
  }, []);

  useEffect(() => {
    searchForFiles();
    loadFileUsageReference();
  }, [searchForFiles, loadFileUsageReference]);

  useEffect(() => {
    loadSavedConfig();
  }, [loadSavedConfig]);

  useEffect(() => {
    const loadCompanyName = async () => {
      try {
        const name = await getCompanyName("combined");
        setCompanyName(name || "ABC Private Limited");
      } catch {
        setCompanyName("ABC Private Limited");
      }
    };
    loadCompanyName();
  }, []);

  useEffect(() => {
    const loadInference = async () => {
      try {
        const result = await (window as any).sharePointAPI?.downloadClientJson?.(
          "Execution_ROU_Responses.json",
        );
        if (result?.success && result?.data?.substantiveProcedures) {
          const inferenceId = "ROU-DISCLOSURE-INFERENCE";
          const inferenceData = result.data.substantiveProcedures[inferenceId];
          if (inferenceData) {
            setInferenceQuestion({
              textResponse: inferenceData.textResponse || null,
              documents: (inferenceData.documents || []).map((docName: string) => ({
                id: `doc_${docName}`,
                type: "linked",
                name: docName,
              })),
            });
          }
        }
      } catch {
        /* ignore */
      }
    };
    loadInference();
  }, []);

  const uploadFile = async () => {
    setUploadStatus("uploading");
    try {
      const input = document.createElement("input");
      input.type = "file";
      input.accept = ".xlsx,.xls,.csv";
      input.onchange = async (e) => {
        const file = (e.target as HTMLInputElement).files?.[0];
        if (file) {
          try {
            const base64Content = await new Promise<string>((resolve, reject) => {
              const reader = new FileReader();
              reader.onload = () => {
                const result = reader.result as string;
                resolve(result.split(",")[1]);
              };
              reader.onerror = () => reject(new Error("Failed to read file"));
              reader.readAsDataURL(file);
            });

            if (window.sharePointAPI?.uploadFile) {
              const fyYear = getFY_YEAR();
              if (!fyYear) {
                throw new Error("FY_YEAR is not configured. Please ensure you have selected an engagement.");
              }

              const result = await window.sharePointAPI.uploadFile({
                fileContent: base64Content,
                fileName: file.name,
                referenceValue: referenceValue,
                folderName: "client",
                fyYear,
                sectionList: [FILE_SECTION],
              });

              if (result.success) {
                setFoundFiles((prev) =>
                  prev.includes(file.name) ? prev : [...prev, file.name],
                );
                setFileSearchStatus("found");
                setUploadStatus("completed");
              } else {
                setUploadStatus("error");
              }
            } else {
              setUploadStatus("error");
            }
          } catch {
            setUploadStatus("error");
          }
        }
      };
      input.click();
    } catch {
      setUploadStatus("error");
    }
  };

  const loadExcelColumns = async (panel: PanelKey) => {
    const selectedFile = selectedFiles[panel];
    if (!selectedFile) return;
    setLoadingCols((prev) => ({ ...prev, [panel]: true }));
    try {
      if (window.payroll?.loadExcelColumns) {
        const result = await window.payroll.loadExcelColumns(selectedFile);
        if (result.ok && result.columns) {
          const columns = result.columns.map((col: string) => ({ name: col, value: col }));
          setExcelColumns((prev) => ({ ...prev, [panel]: columns }));
        }
      }
    } catch {
      setExcelColumns((prev) => ({ ...prev, [panel]: [] }));
    } finally {
      setLoadingCols((prev) => ({ ...prev, [panel]: false }));
    }

    const prefix = `${panel}_file`;
    await updateProcedureFileUsageForWorkspace(WORKSPACE_NAME, PROCEDURE_NAME, [
      { [prefix]: selectedFile },
    ]);
    await loadFileUsageReference();
  };

  const handleMappingChange = (panel: PanelKey, fieldKey: string, columnName: string) => {
    const v = columnName === UNMAP_VALUE ? "" : columnName;
    setColumnMappings((prev) => ({
      ...prev,
      [panel]: { ...prev[panel], [fieldKey]: v },
    }));
  };

  const persistConfig = async () => {
    setProcessingStatus("running");
    setSaveError(null);
    try {
      const api = (window as any).sharePointAPI;
      if (!api?.uploadClientJson || !api?.uploadFile || !api?.downloadClientJson) {
        throw new Error("SharePoint upload/download is not available in this environment.");
      }

      const fyYear = getFY_YEAR();
      if (!fyYear) {
        throw new Error("FY_YEAR is not configured. Please ensure you have selected an engagement.");
      }

      const shaped = ensureMatrixShape(cellMatrix, disclosureCategories);
      const matrixForJson = JSON.parse(JSON.stringify(shaped)) as Record<string, Record<string, number>>;
      applyDerivedRows(matrixForJson, disclosureCategories);

      let reportingDate = (disclosureRepDate || "").trim();
      if (!reportingDate) {
        const discRes = await api.downloadClientJson(DISCLOSURE_OUTPUT_FILE);
        const raw = discRes?.data;
        const list = Array.isArray(raw) ? raw : raw && typeof raw === "object" ? [raw] : [];
        const picked = pickDisclosureEntry(list);
        reportingDate = picked ? String((picked as Record<string, unknown>).reporting_date ?? "").trim() : "";
      }
      if (!reportingDate) {
        throw new Error(
          "Reporting date is unknown. Run register disclosure or load disclosure from SharePoint first.",
        );
      }

      const newDisclosureEntry = buildExecutionRouDisclosureEntry(
        reportingDate,
        matrixForJson,
        disclosureCategories,
      );

      const existingDiscRes = await api.downloadClientJson(DISCLOSURE_OUTPUT_FILE);
      const mergedList = mergeDisclosureJsonList(existingDiscRes?.data, newDisclosureEntry);
      const disclosureJsonText = JSON.stringify(mergedList, null, 4);

      const base64Disclosure = btoa(unescape(encodeURIComponent(disclosureJsonText)));
      const discUpload = await api.uploadFile({
        fileContent: base64Disclosure,
        fileName: DISCLOSURE_OUTPUT_FILE,
        folderName: "juggernaut",
        fyYear,
        referenceValue: "ROU Disclosure — saved from UI",
        sectionList: ["Execution_ROU_Disclosure"],
      });
      if (!discUpload?.success) {
        throw new Error(discUpload?.error || "Failed to upload Execution_ROU_Disclosure.json");
      }

      const payload = {
        version: 1,
        savedAt: new Date().toISOString(),
        register: {
          file: selectedFiles.register,
          mappings: columnMappings.register,
          usefulLifeUnit: usefulLifeUnit || null,
        },
        additions: {
          file: selectedFiles.additions,
          mappings: columnMappings.additions,
        },
        deletions: {
          file: selectedFiles.deletions,
          mappings: columnMappings.deletions,
        },
        disclosureTable: {
          categories: disclosureCategories,
          cellMatrix: matrixForJson,
          reportingDate,
        },
      };

      const uploadRes = await api.uploadClientJson(CONFIG_FILE, payload);
      if (!uploadRes?.success) {
        throw new Error(uploadRes?.error || "Failed to save configuration");
      }

      setDisclosureRepDate(reportingDate);
      setCellMatrix(matrixForJson);

      const usageEntries: FileUsageEntry[] = [];
      (["register", "additions", "deletions"] as PanelKey[]).forEach((p) => {
        if (selectedFiles[p]) usageEntries.push({ [`${p}_file`]: selectedFiles[p] });
        Object.entries(columnMappings[p]).forEach(([k, val]) => {
          if (val) usageEntries.push({ [`${p}_${k}`]: val });
        });
      });
      if (usefulLifeUnit) usageEntries.push({ useful_life_unit: usefulLifeUnit });
      if (usageEntries.length > 0) {
        await updateProcedureFileUsageForWorkspace(WORKSPACE_NAME, PROCEDURE_NAME, usageEntries);
        await loadFileUsageReference();
      }

      setProcessingStatus("completed");
      setSaveStatus("saved");
      setTimeout(() => {
        setSaveStatus("idle");
        setProcessingStatus("idle");
      }, 2500);
    } catch (e) {
      setProcessingStatus("error");
      setSaveError(e instanceof Error ? e.message : "Save failed");
      setSaveStatus("error");
    }
  };

  const displayMatrix = useMemo(() => {
    const shaped = ensureMatrixShape(cellMatrix, disclosureCategories);
    if (disclosureEditMode) return shaped;
    const clone = JSON.parse(JSON.stringify(shaped)) as Record<string, Record<string, number>>;
    applyDerivedRows(clone, disclosureCategories);
    return clone;
  }, [cellMatrix, disclosureCategories, disclosureEditMode]);

  const setDisclosureCellValue = useCallback(
    (rowId: string, colId: string, raw: string) => {
      const cleaned = raw.replace(/,/g, "").replace(/\s/g, "").trim();
      let v = 0;
      if (cleaned !== "" && cleaned !== "-") {
        const n = Number(cleaned);
        v = Number.isFinite(n) ? Math.round(n) : 0;
      }
      setCellMatrix((prev) => {
        const next = { ...prev };
        next[rowId] = { ...(next[rowId] || {}) };
        next[rowId][colId] = v;
        const shaped = ensureMatrixShape(next, disclosureCategories);
        if (!disclosureEditMode) applyDerivedRows(shaped, disclosureCategories);
        return shaped;
      });
    },
    [disclosureCategories, disclosureEditMode],
  );

  const finishDisclosureEdit = useCallback(() => {
    setDisclosureEditMode(false);
    setCellMatrix((prev) => {
      const shaped = ensureMatrixShape(prev, disclosureCategories);
      applyDerivedRows(shaped, disclosureCategories);
      return shaped;
    });
  }, [disclosureCategories]);

  const runRegisterDisclosure = async () => {
    setRegisterRunStatus("running");
    setRegisterRunError(null);
    try {
      if (!window.payroll?.run) {
        throw new Error("Python run is not available in this environment.");
      }
      const registerKeys = REGISTER_FIELDS.map((f) => f.key);
      const addKeys = ADDITIONS_FIELDS.map((f) => f.key);
      const delKeys = DELETIONS_FIELDS.map((f) => f.key);
      const result = await window.payroll.run("execute_rou_disclosure", {
        inputFiles: [],
        options: {
          rou_file: selectedFiles.register || "",
          add_file: selectedFiles.additions || "",
          del_file: selectedFiles.deletions || "",
          rou_map: buildScriptColumnMap(columnMappings.register, registerKeys),
          add_map: buildScriptColumnMap(columnMappings.additions, addKeys),
          del_map: buildScriptColumnMap(columnMappings.deletions, delKeys),
          useful_life_unit: usefulLifeUnit || "",
          engagement_file: "EngagementDetails.json",
          output_file_name: DISCLOSURE_OUTPUT_FILE,
          input_folder: "client",
          output_folder: "juggernaut",
          db_section: ["Execution_ROU_Disclosure"],
          reference_value: "ROU Disclosure — register run",
        },
      });
      if (!result?.ok) throw new Error((result as { error?: string })?.error || "Run failed");

      const dl = await (window as any).sharePointAPI?.downloadClientJson?.(DISCLOSURE_OUTPUT_FILE);
      if (!dl?.success) throw new Error(dl?.error || "Could not download disclosure JSON.");

      const merged = buildDisclosureTableStateFromExecutionJson(dl.data, undefined);
      if (!merged) {
        throw new Error("Disclosure JSON has no category data for the latest reporting date.");
      }
      setDisclosureCategories(merged.categories);
      setDisclosureRepDate(merged.reportingDate);

      setCellMatrix((prev) => {
        const again = buildDisclosureTableStateFromExecutionJson(dl.data, prev);
        return again ? again.cellMatrix : merged.cellMatrix;
      });

      setRegisterRunStatus("ok");
    } catch (e) {
      setRegisterRunStatus("err");
      setRegisterRunError(e instanceof Error ? e.message : "Run failed");
    }
  };

  const runLeaseDepositColumns = async () => {
    setLeaseRunStatus("running");
    setLeaseRunError(null);
    try {
      const api = (window as any).sharePointAPI;
      if (!api?.downloadClientJson) throw new Error("downloadClientJson is not available.");

      let rep = disclosureRepDate;
      if (!rep) {
        const dl = await api.downloadClientJson(DISCLOSURE_OUTPUT_FILE);
        const raw = dl?.data;
        const list = Array.isArray(raw) ? raw : raw && typeof raw === "object" ? [raw] : [];
        const entry = pickDisclosureEntry(list);
        rep = entry ? String(entry.reporting_date ?? "") : null;
      }
      if (!rep) {
        throw new Error("Reporting date is unknown. Run register disclosure first (or ensure disclosure JSON exists).");
      }
      const repD = parseRepDateDdMmYyyy(rep);
      if (!repD) throw new Error(`Invalid reporting date: ${rep}`);

      const [assRes, modRes, reassRes, modInpRes, reassInpRes, sdRes] = await Promise.all([
        api.downloadClientJson("Execution_LEASE_AssessmentOut.json"),
        api.downloadClientJson("Execution_LEASE_Modification.json"),
        api.downloadClientJson("Execution_LEASE_Reassessment.json"),
        api.downloadClientJson("Execution_LEASE_ModificationInp.json"),
        api.downloadClientJson("Execution_LEASE_ReassessmentInp.json"),
        api.downloadClientJson("Execution_OFA_SDOut.json"),
      ]);

      const fig = computeRouLeaseDepositColumns(
        repD,
        assRes?.data,
        modRes?.data,
        reassRes?.data,
        sdRes?.data,
        modInpRes?.data,
        reassInpRes?.data,
      );

      setCellMatrix((prev) => {
        const base = ensureMatrixShape(prev, disclosureCategories);
        const L = fig.leases;
        const D = fig.deposits;
        base.gb_opening.leases = L.gbOpening;
        base.gb_additions.leases = L.gbAdditions;
        base.gb_transfers.leases = L.gbTransfers;
        base.ad_opening.leases = L.adOpening;
        base.ad_depreciation.leases = L.adDepreciationCy;
        base.ad_eliminations.leases = L.adEliminations;

        base.gb_opening.deposits = D.gbOpening;
        base.gb_additions.deposits = D.gbAdditions;
        base.gb_transfers.deposits = D.gbTransfers;
        base.ad_opening.deposits = D.adOpening;
        base.ad_depreciation.deposits = D.adDepreciationCy;
        base.ad_eliminations.deposits = D.adEliminations;

        applyDerivedRows(base, disclosureCategories);
        return base;
      });

      setLeaseRunStatus("ok");
    } catch (e) {
      setLeaseRunStatus("err");
      setLeaseRunError(e instanceof Error ? e.message : "Lease/deposit run failed");
    }
  };

  const saveInferenceQuestion = async () => {
    if (!companyName) {
      setInferenceSaveError("Company name is required");
      setInferenceSaveStatus("error");
      return;
    }

    setInferenceSaveStatus("saving");
    setInferenceSaveError(null);

    try {
      const documentManagers = document.querySelectorAll("[data-document-manager]");
      for (const manager of Array.from(documentManagers)) {
        const itemId = manager.getAttribute("data-item-id");
        if (itemId) {
          const autoSaveMethod = (window as any)[`autoSaveTextbox_${itemId}`];
          if (typeof autoSaveMethod === "function") autoSaveMethod();
        }
      }
      await new Promise((r) => setTimeout(r, 1500));

      const areaPrefix = "ROU";
      const fileName = `Execution_${areaPrefix}_Responses.json`;
      let existingData: any = {};
      try {
        const downloadResult = await (window as any).sharePointAPI?.downloadClientJson?.(fileName);
        if (downloadResult?.success && downloadResult?.data) {
          existingData = downloadResult.data;
        }
      } catch {
        /* start fresh */
      }
      if (!existingData || typeof existingData !== "object" || Array.isArray(existingData)) {
        existingData = {};
      }
      if (!existingData.substantiveProcedures) existingData.substantiveProcedures = {};

      const inferenceId = "ROU-DISCLOSURE-INFERENCE";
      const sectionToFind = `Execution_${areaPrefix}_${inferenceId}`;
      let inferenceDocumentNames: string[] = [];
      try {
        if ((window as any).sharePointAPI?.loadDocumentListBySection) {
          const dbResult = await (window as any).sharePointAPI.loadDocumentListBySection(
            sectionToFind,
          );
          if (dbResult?.success && dbResult?.data && Array.isArray(dbResult.data)) {
            inferenceDocumentNames = dbResult.data
              .map((file: any) => file.name || "")
              .filter((name: string) => name && name.trim().length > 0);
          }
        }
      } catch {
        /* ignore */
      }

      const inferenceTextboxFromLocalStorage = localStorage.getItem(`textbox_${inferenceId}`);
      const hasInferenceTextboxContent =
        inferenceTextboxFromLocalStorage && inferenceTextboxFromLocalStorage.trim().length > 0;

      let inferenceTextResponseData = null;
      if (hasInferenceTextboxContent) {
        inferenceTextResponseData = {
          html: inferenceTextboxFromLocalStorage,
          plainText: inferenceTextboxFromLocalStorage.replace(/<[^>]*>/g, ""),
        };
      } else if (inferenceQuestion.textResponse) {
        inferenceTextResponseData = inferenceQuestion.textResponse;
      }

      existingData.substantiveProcedures[inferenceId] = {
        selectedOption: "",
        textResponse: inferenceTextResponseData,
        documents: inferenceDocumentNames,
        table: null,
        lastUpdated: new Date().toISOString(),
        companyName,
      };

      const jsonString = JSON.stringify(existingData, null, 2);
      const base64Content = btoa(unescape(encodeURIComponent(jsonString)));
      const fyYear = getFY_YEAR();
      if (!fyYear) {
        throw new Error("FY_YEAR is not configured. Please ensure you have selected an engagement.");
      }

      const uploadResult = await (window as any).sharePointAPI?.uploadFile?.({
        fileContent: base64Content,
        fileName,
        referenceValue: `Execution ${areaPrefix} Substantive Responses`,
        folderName: "juggernaut",
        fyYear,
        sectionList: [`Execution_${areaPrefix}_Responses`],
      });

      if (!uploadResult?.success) {
        throw new Error(uploadResult?.error || "Failed to save inference question");
      }

      setInferenceSaveStatus("saved");
      setTimeout(() => setInferenceSaveStatus("idle"), 3000);
    } catch (error) {
      console.error("Failed to save inference question:", error);
      setInferenceSaveError(error instanceof Error ? error.message : "Unknown error");
      setInferenceSaveStatus("error");
    }
  };

  const renderColumnSelect = (
    panel: PanelKey,
    fieldKey: string,
    fieldLabel: string,
  ) => {
    const cols = excelColumns[panel];
    const mapped = columnMappings[panel][fieldKey] || "";
    const usageKey = `${panel}_${fieldKey}`;
    const lastUsed = fileUsageReference.find((e) => e[usageKey])?.[usageKey];

    return (
      <div key={`${panel}-${fieldKey}`} className="space-y-2">
        <Label className="text-white">{fieldLabel}</Label>
        <Select
          value={mapped || UNMAP_VALUE}
          onValueChange={(v) => handleMappingChange(panel, fieldKey, v)}
          disabled={cols.length === 0}
        >
          <SelectTrigger>
            <SelectValue
              placeholder={cols.length === 0 ? "Load columns first (optional)" : "Map column (optional)"}
            />
          </SelectTrigger>
          <SelectContent className="border-white/10 bg-black/90 text-white">
            <SelectItem value={UNMAP_VALUE}>— Not mapped —</SelectItem>
            {cols.map((col) => (
              <SelectItem key={col.value} value={col.value}>
                {col.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {lastUsed ? (
          <p className="mt-1 text-xs text-white/60">Last used: {lastUsed}</p>
        ) : null}
      </div>
    );
  };

  const renderUsefulLifeUnitSelect = () => {
    const lastUsed = fileUsageReference.find((e) => e.useful_life_unit)?.useful_life_unit;
    return (
      <div className="space-y-2">
        <Label className="text-white">Useful life is expressed in (optional)</Label>
        <Select
          value={usefulLifeUnit ? usefulLifeUnit : USEFUL_LIFE_UNIT_NONE}
          onValueChange={(v) =>
            setUsefulLifeUnit(v === USEFUL_LIFE_UNIT_NONE ? "" : (v as "months" | "years"))
          }
        >
          <SelectTrigger>
            <SelectValue placeholder="Select unit (optional)" />
          </SelectTrigger>
          <SelectContent className="border-white/10 bg-black/90 text-white">
            <SelectItem value={USEFUL_LIFE_UNIT_NONE}>— Not specified —</SelectItem>
            <SelectItem value="months">Months</SelectItem>
            <SelectItem value="years">Years</SelectItem>
          </SelectContent>
        </Select>
        {lastUsed ? (
          <p className="mt-1 text-xs text-white/60">Last used: {lastUsed}</p>
        ) : null}
      </div>
    );
  };

  const renderPanel = (
    panel: PanelKey,
    title: string,
    fields: Array<{ key: string; label: string }>,
    step1Comment: string,
    step2Comment: string,
    nested?: boolean,
  ) => {
    const fileVal = selectedFiles[panel] || NO_FILE_VALUE;
    const lastFile = fileUsageReference.find((e) => e[`${panel}_file`])?.[`${panel}_file`];

    const inner = (
      <>
          <div>
            <div className="mb-2 flex items-center justify-between">
              <h4 className="flex items-center gap-2 text-sm font-semibold text-white">
                <Search className="h-4 w-4 text-blue-500" />
                Select file (optional)
              </h4>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setReviewCommentQuestionId(getSectionQuestionId(step1Comment))}
                className="h-7 w-7 flex-shrink-0 p-1 text-yellow-400 hover:bg-white/10 hover:text-yellow-300"
                title="Review Comment"
              >
                <MessageSquare className="h-4 w-4" />
              </Button>
            </div>
            <div className="space-y-2">
              <Label className="text-white">File from {FILE_SECTION}</Label>
              <Select
                value={fileVal}
                onValueChange={(v) =>
                  setSelectedFiles((prev) => ({
                    ...prev,
                    [panel]: v === NO_FILE_VALUE ? "" : v,
                  }))
                }
                disabled={fileSearchStatus !== "found" && foundFiles.length === 0}
              >
                <SelectTrigger>
                  <SelectValue placeholder="No file selected" />
                </SelectTrigger>
                <SelectContent className="border-white/10 bg-black/90 text-white">
                  <SelectItem value={NO_FILE_VALUE}>No file selected</SelectItem>
                  {foundFiles.map((f) => (
                    <SelectItem key={`${panel}-${f}`} value={f}>
                      {f}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {lastFile ? (
                <p className="text-xs text-white/60">Last used file: {lastFile}</p>
              ) : null}
              {selectedFiles[panel] ? (
                <div className="flex items-center gap-3 pt-2">
                  <Button
                    type="button"
                    onClick={() => loadExcelColumns(panel)}
                    disabled={loadingCols[panel]}
                    className="bg-blue-600 text-white hover:bg-blue-700"
                  >
                    {loadingCols[panel] ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading Columns...
                      </>
                    ) : (
                      "Load Columns"
                    )}
                  </Button>
                  {excelColumns[panel].length > 0 ? (
                    <span className="text-sm text-green-400">
                      {excelColumns[panel].length} columns loaded
                    </span>
                  ) : null}
                </div>
              ) : null}
            </div>
          </div>

          <div>
            <div className="mb-2 flex items-center justify-between">
              <h4 className="text-sm font-semibold text-white">Map columns (all optional)</h4>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setReviewCommentQuestionId(getSectionQuestionId(step2Comment))}
                className="h-7 w-7 flex-shrink-0 p-1 text-yellow-400 hover:bg-white/10 hover:text-yellow-300"
                title="Review Comment"
              >
                <MessageSquare className="h-4 w-4" />
              </Button>
            </div>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              {fields.map(({ key, label }) => renderColumnSelect(panel, key, label))}
              {panel === "register" ? renderUsefulLifeUnitSelect() : null}
            </div>
          </div>
      </>
    );

    if (nested) {
      return (
        <div
          key={panel}
          className="rounded-lg border border-white/10 bg-black/30 p-4 md:p-5"
        >
          <h3 className="mb-4 text-lg font-semibold text-white">{title}</h3>
          <div className="space-y-6">{inner}</div>
        </div>
      );
    }

    return (
      <Card key={panel} className="border-white/10 bg-black/40">
        <CardHeader>
          <CardTitle className="text-lg text-white">{title}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">{inner}</CardContent>
      </Card>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">ROU Disclosure — Right-of-Use Assets</h2>
          <p className="text-gray-400">
            Map optional columns for each listing. Files are listed from {FILE_SECTION}.
          </p>
        </div>
        <div className="flex gap-2">
          {setActiveSection ? (
            <Button variant="outline" onClick={() => setActiveSection("rou-substantive")}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back
            </Button>
          ) : null}
        </div>
      </div>

      <Card className="border-white/10 bg-black/40">
        <CardHeader className={`space-y-0 ${rouFilesSectionOpen ? "pb-0" : "pb-5"}`}>
          <div className="flex items-start justify-between gap-2">
            <button
              type="button"
              onClick={() => setRouFilesSectionOpen((o) => !o)}
              className="flex w-full min-w-0 items-center justify-between gap-3 rounded-lg text-left outline-none ring-offset-background transition hover:bg-white/5 focus-visible:ring-2 focus-visible:ring-cyan-500/50"
              aria-expanded={rouFilesSectionOpen}
            >
              <div className="min-w-0 flex-1">
                <CardTitle className="flex flex-wrap items-center gap-2 text-lg text-white">
                  <Search className="h-5 w-5 shrink-0 text-blue-500" />
                  <span>
                    IPE Testing
                  </span>
                </CardTitle>
                <p className="mt-2 text-sm text-white/55">
                  Search the file library, map columns for each listing, then run the register disclosure
                  script.
                </p>
              </div>
              <ChevronDown
                className={`h-5 w-5 shrink-0 text-slate-400 transition-transform duration-200 ${
                  rouFilesSectionOpen ? "rotate-180" : ""
                }`}
              />
            </button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                setReviewCommentQuestionId(getSectionQuestionId("Step 1 Search Files"));
              }}
              className="h-7 w-7 shrink-0 p-1 text-yellow-400 hover:bg-white/10 hover:text-yellow-300"
              title="Review Comment"
            >
              <MessageSquare className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        {rouFilesSectionOpen ? (
          <CardContent className="space-y-6 pt-6">
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                {fileSearchStatus === "searching" ? (
                  <div className="flex items-center gap-2 text-blue-400">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span>Searching for files...</span>
                  </div>
                ) : null}
                {fileSearchStatus === "found" ? (
                  <span className="font-bold text-green-400">FOUND!</span>
                ) : null}
                {fileSearchStatus === "not_found" ? (
                  <span className="text-yellow-400">No files found</span>
                ) : null}
                {fileSearchStatus === "idle" ? (
                  <span className="text-gray-400">Searching for files...</span>
                ) : null}
                {fileSearchStatus === "found" || foundFiles.length > 0 ? (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="ml-auto border-white/20 text-white"
                    onClick={() => searchForFiles()}
                  >
                    Refresh list
                  </Button>
                ) : null}
              </div>

              {fileSearchStatus === "not_found" || foundFiles.length === 0 ? (
                <div className="space-y-4 rounded-lg border border-yellow-500/20 bg-yellow-500/5 p-4">
                  <div className="font-medium text-yellow-400">
                    Upload a file to {FILE_SECTION} if none are available.
                  </div>
                  <div className="space-y-3">
                    <div className="space-y-2">
                      <Label className="text-white">Reference value (optional)</Label>
                      <Input
                        value={referenceValue}
                        onChange={(e) => setReferenceValue(e.target.value)}
                        placeholder="Enter reference value"
                        className="border-white/10 bg-black/40 text-white"
                      />
                    </div>
                    <Button
                      type="button"
                      onClick={uploadFile}
                      disabled={uploadStatus === "uploading" || isArchivalMode}
                      className="bg-green-600 text-white hover:bg-green-700"
                    >
                      {uploadStatus === "uploading" ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Uploading...
                        </>
                      ) : (
                        <>
                          <Upload className="mr-2 h-4 w-4" /> Upload file
                        </>
                      )}
                    </Button>
                    {uploadStatus === "completed" ? (
                      <span className="text-green-400">File uploaded successfully.</span>
                    ) : null}
                    {uploadStatus === "error" ? (
                      <span className="text-red-400">Upload failed</span>
                    ) : null}
                  </div>
                </div>
              ) : null}

              {foundFiles.length > 0 ? (
                <p className="text-sm text-white/70">
                  {foundFiles.length} file(s) available. Choose a file per listing below (optional).
                </p>
              ) : null}
            </div>

            <div className="space-y-6">
              {renderPanel(
                "register",
                "ROU Asset Register",
                REGISTER_FIELDS,
                "Register Step 1 File",
                "Register Step 2 Map Columns",
                true,
              )}
              {renderPanel(
                "additions",
                "Additions Listing",
                ADDITIONS_FIELDS,
                "Additions Step 1 File",
                "Additions Step 2 Map Columns",
                true,
              )}
              {renderPanel(
                "deletions",
                "Deletions Listing",
                DELETIONS_FIELDS,
                "Deletions Step 1 File",
                "Deletions Step 2 Map Columns",
                true,
              )}
            </div>

            <div className="border-t border-white/10 pt-6">
              <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
                <Button
                  type="button"
                  size="sm"
                  className="w-full bg-green-600 text-white hover:bg-green-700 sm:w-auto"
                  disabled={isArchivalMode || registerRunStatus === "running"}
                  onClick={() => void runRegisterDisclosure()}
                >
                  {registerRunStatus === "running" ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Running…
                    </>
                  ) : (
                    <>
                      <Play className="mr-2 h-4 w-4" /> Run register disclosure
                    </>
                  )}
                </Button>
                {registerRunStatus === "err" && registerRunError ? (
                  <p className="text-sm text-red-400">{registerRunError}</p>
                ) : null}
                {registerRunStatus === "ok" ? (
                  <p className="text-sm text-green-400">
                    Register disclosure JSON updated on SharePoint.
                  </p>
                ) : null}
              </div>
            </div>
          </CardContent>
        ) : null}
      </Card>

      <Card className="border-white/10 bg-black/40">
        <CardHeader>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <CardTitle className="text-white">ROU disclosure table</CardTitle>
              <p className="mt-1 text-sm text-white/60">
                Asset categories from the register run; last columns are leases and security deposits.
                {disclosureRepDate ? (
                  <span className="ml-2 text-white/80">Reporting date: {disclosureRepDate}</span>
                ) : null}
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="border-white/20 text-white"
                disabled={isArchivalMode || disclosureEditMode}
                onClick={() => setDisclosureEditMode(true)}
              >
                <Pencil className="mr-2 h-4 w-4" />
                Edit
              </Button>
              {disclosureEditMode ? (
                <Button
                  type="button"
                  size="sm"
                  className="bg-slate-600 text-white hover:bg-slate-500"
                  onClick={finishDisclosureEdit}
                >
                  Done editing
                </Button>
              ) : null}
              <Button
                type="button"
                size="sm"
                className="bg-cyan-700 text-white hover:bg-cyan-600"
                disabled={isArchivalMode || leaseRunStatus === "running"}
                onClick={() => void runLeaseDepositColumns()}
              >
                {leaseRunStatus === "running" ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Running…
                  </>
                ) : (
                  <>
                    <Play className="mr-2 h-4 w-4" /> Refresh leases &amp; deposits
                  </>
                )}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {leaseRunStatus === "err" && leaseRunError ? (
            <p className="text-sm text-red-400">{leaseRunError}</p>
          ) : null}
          {leaseRunStatus === "ok" ? (
            <p className="text-sm text-green-400">Leases and deposits columns refreshed.</p>
          ) : null}

          {/* border-separate (not border-collapse) is required for sticky first column in tables */}
          <div className="relative isolate overflow-x-auto overflow-y-visible overscroll-x-contain rounded-lg border border-white/10">
            <table className="w-max min-w-full border-separate border-spacing-0 text-sm text-white">
              <thead>
                <tr className="border-b border-white/10">
                  <th
                    scope="col"
                    className="sticky left-0 z-30 w-[min(240px,42vw)] min-w-[200px] max-w-[280px] border-b border-r border-white/10 bg-neutral-950 px-3 py-2 text-left text-base font-medium shadow-[4px_0_12px_-2px_rgba(0,0,0,0.65)]"
                  >
                    Particulars
                  </th>
                  {disclosureCategories.map((c) => (
                    <th
                      key={c}
                      scope="col"
                      className="min-w-[120px] border-b border-r border-white/10 bg-black/50 px-2 py-2 text-right font-medium"
                    >
                      {c}
                    </th>
                  ))}
                  <th
                    scope="col"
                    className="min-w-[120px] border-b border-r border-white/10 bg-black/50 px-2 py-2 text-right font-medium"
                  >
                    Leases
                  </th>
                  <th
                    scope="col"
                    className="min-w-[120px] border-b border-white/10 bg-black/50 px-2 py-2 text-right font-medium"
                  >
                    Deposits
                  </th>
                </tr>
              </thead>
              <tbody>
                {DISCLOSURE_ROW_DEFS.map((def) => {
                  const scrollableColSpan = disclosureCategories.length + 2;
                  if (def.section) {
                    return (
                      <tr key={def.id} className="bg-black/40">
                        <td
                          className="sticky left-0 z-20 w-[min(240px,42vw)] min-w-[200px] max-w-[280px] border-b border-r border-white/10 bg-neutral-950 px-3 py-2 align-middle font-semibold text-white shadow-[4px_0_12px_-2px_rgba(0,0,0,0.65)]"
                        >
                          {def.label}
                        </td>
                        <td
                          colSpan={scrollableColSpan}
                          className="border-b border-white/10 bg-black/40 px-3 py-2"
                          aria-hidden
                        />
                      </tr>
                    );
                  }
                  return (
                    <tr
                      key={def.id}
                      className="group border-b border-white/5 hover:bg-white/[0.03]"
                    >
                      <td className="sticky left-0 z-10 w-[min(240px,42vw)] min-w-[200px] max-w-[280px] border-b border-r border-white/10 bg-neutral-950 px-3 py-2 align-middle text-white/90 shadow-[4px_0_12px_-2px_rgba(0,0,0,0.65)] group-hover:bg-neutral-900">
                        {def.label}
                      </td>
                      {disclosureCategories.map((c) => (
                        <td
                          key={`${def.id}-${c}`}
                          className="border-b border-r border-white/10 bg-black/40 px-1 py-1 align-middle group-hover:bg-white/[0.03]"
                        >
                          {disclosureEditMode ? (
                            <Input
                              className="h-8 border-white/20 bg-black/50 text-right text-sm text-white"
                              value={String(cellMatrix[def.id]?.[c] ?? 0)}
                              onChange={(e) => setDisclosureCellValue(def.id, c, e.target.value)}
                              disabled={isArchivalMode}
                            />
                          ) : (
                            <span className="block pr-2 text-right tabular-nums">
                              {formatIntComma(displayMatrix[def.id]?.[c] ?? 0)}
                            </span>
                          )}
                        </td>
                      ))}
                      {(["leases", "deposits"] as const).map((sp) => (
                        <td
                          key={`${def.id}-${sp}`}
                          className="border-b border-r border-white/10 bg-black/40 px-1 py-1 last:border-r-0 group-hover:bg-white/[0.03]"
                        >
                          {disclosureEditMode ? (
                            <Input
                              className="h-8 border-white/20 bg-black/50 text-right text-sm text-white"
                              value={String(cellMatrix[def.id]?.[sp] ?? 0)}
                              onChange={(e) => setDisclosureCellValue(def.id, sp, e.target.value)}
                              disabled={isArchivalMode}
                            />
                          ) : (
                            <span className="block pr-2 text-right tabular-nums">
                              {formatIntComma(displayMatrix[def.id]?.[sp] ?? 0)}
                            </span>
                          )}
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-white/50">
            Leases — accumulated depreciation opening is total Depreciation before the current FY using the same
            assessment / modification / reassessment month stream as lease disclosure Accretion of Interest. Gross
            block transfers: sum of ROU-only amounts from the same journal logic as Lease Liability – Ind AS for every
            modification and reassessment input row whose event falls in the current FY (signs kept; profit and loss
            lines not included).
            Closing balances and net block follow your mapping: register and deposits use gross closing = opening +
            additions − transfers; leases use gross closing = opening + additions + transfers. Accumulated
            depreciation closing = opening + depreciation for the year + eliminations (all columns). Use{" "}
            <span className="font-medium text-white/70">Save ROU disclosure configuration</span> below to
            push this table to SharePoint.
          </p>
        </CardContent>
      </Card>

      <Card className="border-white/10 bg-black/40">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-3 text-white">
              <Play className="h-5 w-5 text-green-500" />
              Save configuration
            </CardTitle>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() =>
                setReviewCommentQuestionId(getSectionQuestionId("Save configuration"))
              }
              className="h-7 w-7 flex-shrink-0 p-1 text-yellow-400 hover:bg-white/10 hover:text-yellow-300"
              title="Review Comment"
            >
              <MessageSquare className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-white/60">
            Builds <span className="font-mono text-white/80">{DISCLOSURE_OUTPUT_FILE}</span> from the
            disclosure table (all asset categories plus leases and deposits), merges by reporting date,
            uploads to the juggernaut folder (updates{" "}
            <span className="font-mono text-white/80">db.json</span>), and saves file mappings to{" "}
            <span className="font-mono text-white/80">{CONFIG_FILE}</span>. A reporting date is required
            (from register run or existing disclosure on SharePoint).
          </p>
          <div className="flex flex-wrap items-center gap-3">
            <Button
              type="button"
              onClick={persistConfig}
              disabled={isArchivalMode || processingStatus === "running"}
              className="bg-green-600 text-white hover:bg-green-700"
            >
              {processingStatus === "running" ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Saving...
                </>
              ) : (
                <>
                  <Save className="mr-2 h-4 w-4" />
                  Save ROU disclosure configuration
                </>
              )}
            </Button>
            {processingStatus === "completed" || saveStatus === "saved" ? (
              <span className="flex items-center gap-1 text-green-400">
                <CheckCircle className="h-4 w-4" /> Saved
              </span>
            ) : null}
            {processingStatus === "error" || saveStatus === "error" ? (
              <span className="flex items-center gap-1 text-red-400">
                <AlertCircle className="h-4 w-4" /> {saveError || "Failed"}
              </span>
            ) : null}
          </div>
        </CardContent>
      </Card>

      <Card className="border-white/10 bg-black/40">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-white">
              Document the inference and conclusion from the above procedure performed.
            </CardTitle>
            <div className="flex items-center gap-2">
              {inferenceSaveStatus === "saved" ? (
                <span className="text-sm text-green-400">Saved!</span>
              ) : null}
              {inferenceSaveStatus === "error" && inferenceSaveError ? (
                <span className="text-sm text-red-400">Error</span>
              ) : null}
              <Button
                type="button"
                onClick={saveInferenceQuestion}
                disabled={inferenceSaveStatus === "saving" || !companyName || isArchivalMode}
                size="sm"
                className="bg-green-600 text-white hover:bg-green-700 disabled:opacity-50"
              >
                {inferenceSaveStatus === "saving" ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="mr-2 h-4 w-4" />
                    Save
                  </>
                )}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <DocumentManager
            sectionKey="rou-disclosure"
            itemId="ROU-DISCLOSURE-INFERENCE"
            itemQuestion="Document the inference and conclusion from the above procedure performed."
            itemTextResponse={inferenceQuestion.textResponse}
            itemDocuments={inferenceQuestion.documents || []}
            onTextResponseChange={(textResponse) =>
              setInferenceQuestion({ ...inferenceQuestion, textResponse })
            }
            onDocumentsChange={(documents) =>
              setInferenceQuestion({ ...inferenceQuestion, documents })
            }
            disabled={false}
          />
        </CardContent>
      </Card>

      {reviewCommentQuestionId ? (
        <ReviewComment
          questionId={reviewCommentQuestionId}
          questionText={reviewCommentQuestionId}
          isOpen={!!reviewCommentQuestionId}
          onClose={() => setReviewCommentQuestionId(null)}
        />
      ) : null}
    </div>
  );
}
